import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/AuthService';

const BASE_SWIPE_ORDER = ['/', '/results', '/history', '/leaderboard'];
const AUTH_SWIPE_PAGES = ['/achievements', '/profile'];

// Logros y Perfil solo son navegables por swipe con sesión iniciada, igual que
// ya ocurre con sus entradas en el menú de Header.
export function getSwipeOrder(isAuthenticated) {
  return isAuthenticated ? [...BASE_SWIPE_ORDER, ...AUTH_SWIPE_PAGES] : BASE_SWIPE_ORDER;
}

const SWIPE_THRESHOLD_PX = 60;
const SWIPE_DIRECTION_RATIO = 1.5;
const EDGE_RESISTANCE_DIVISOR = 4;

// offsetPx es la posición continua del carrusel en píxeles absolutos (la página
// de índice i descansa en i * anchoDelContenedor). Al no depender nunca del
// índice activo para calcularse, cambiar de página (navigate) no obliga a
// "re-basar" el valor a otro sistema de coordenadas a mitad de la animación
// — la transición CSS simplemente continúa hacia el nuevo destino.
export default function useSwipeNavigation(container) {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartRef = useRef(null);
  const isHorizontalDragRef = useRef(false);
  const dragStartOffsetRef = useRef(0);
  const [offsetPx, setOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const isAuthenticated = authService.isAuthenticated();
  const swipeOrder = getSwipeOrder(isAuthenticated);
  const activeIndex = swipeOrder.indexOf(location.pathname);

  // Mantiene offsetPx sincronizado con la ruta activa cuando la navegación no
  // viene de un gesto (clic en el menú, enlaces, retroceso del navegador...).
  useEffect(() => {
    if (!container || activeIndex === -1) return;
    setOffsetPx(activeIndex * container.clientWidth);
  }, [container, activeIndex]);

  useEffect(() => {
    const el = container;
    if (!el) return;

    const handleTouchStart = (e) => {
      const idx = swipeOrder.indexOf(location.pathname);
      if (idx === -1) return;
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      isHorizontalDragRef.current = false;
      dragStartOffsetRef.current = idx * el.clientWidth;
    };

    const handleTouchMove = (e) => {
      const start = touchStartRef.current;
      if (!start) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      if (!isHorizontalDragRef.current) {
        if (Math.abs(deltaX) < 10 || Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_DIRECTION_RATIO) return;
        isHorizontalDragRef.current = true;
      }

      // Arrastrar a la izquierda avanza en la secuencia, a la derecha retrocede
      const idx = swipeOrder.indexOf(location.pathname);
      const nextIndex = deltaX < 0 ? idx + 1 : idx - 1;
      const hasTarget = nextIndex >= 0 && nextIndex < swipeOrder.length;

      setIsDragging(true);
      setOffsetPx(dragStartOffsetRef.current - (hasTarget ? deltaX : deltaX / EDGE_RESISTANCE_DIVISOR));
    };

    const handleTouchEnd = (e) => {
      const start = touchStartRef.current;
      const wasHorizontalDrag = isHorizontalDragRef.current;
      touchStartRef.current = null;
      isHorizontalDragRef.current = false;
      setIsDragging(false);

      if (!start || !wasHorizontalDrag) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      const idx = swipeOrder.indexOf(location.pathname);
      const nextIndex = deltaX < 0 ? idx + 1 : idx - 1;
      const hasTarget = idx !== -1 && nextIndex >= 0 && nextIndex < swipeOrder.length;
      const passesThreshold =
        Math.abs(deltaX) >= SWIPE_THRESHOLD_PX && Math.abs(deltaX) >= Math.abs(deltaY) * SWIPE_DIRECTION_RATIO;

      if (passesThreshold && hasTarget) {
        setOffsetPx(nextIndex * el.clientWidth);
        navigate(swipeOrder[nextIndex]);
      } else {
        setOffsetPx(dragStartOffsetRef.current);
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [container, navigate, location.pathname, isAuthenticated]);

  return { offsetPx, isDragging };
}
